/**
 * Fixed-point decimals held as integers.
 *
 * A decimal is stored as an integer scaled by 10^places - 3.45 at two places is
 * 345 - so arithmetic on it is exact integer arithmetic. The point is inserted
 * on the way to the page and never during a calculation. Adding 3.45 to 2.18 in
 * binary floating point is 5.630000000000001, and a worksheet answer key is the
 * worst possible place to find that out.
 *
 * Extracted from MoneyGenerator, which held every amount in whole cents for the
 * same reason; formatScaled generalises formatCents over the place count.
 */

/**
 * 3.45, 2 -> 345. Rounds, so a value that is already scaled passes through.
 *
 * The generators never call this - they draw scaled integers directly and never
 * hold a decimal as a float at all, which is the point. It exists as the inverse
 * of formatScaled for tests that need to check printed output against the
 * arithmetic, and for reading a decimal in from outside (ADR 0006 needs it to
 * compare two written values).
 */
export function toScaled(value: number, places: number): number {
    return Math.round(value * 10 ** places);
}

/**
 * 345, 2 -> "3.45". Always writes exactly `places` decimals, so trailing zeros
 * are kept and columns line up down the page. Handles a negative sign, a zero
 * whole part (0.07, not .07), and places === 0 (no point at all).
 */
export function formatScaled(scaled: number, places: number): string {
    const sign = scaled < 0 ? "-" : "";
    const absolute = Math.abs(Math.trunc(scaled));

    if (places <= 0) {
        return `${sign}${absolute}`;
    }

    const divisor = 10 ** places;
    const whole = Math.floor(absolute / divisor);
    const fraction = absolute % divisor;
    return `${sign}${whole}.${String(fraction).padStart(places, "0")}`;
}

/**
 * Moves a scaled integer from one place count to another. Widening is exact;
 * narrowing rounds, and is only ever asked for when the dropped digits are
 * known to be zero (trimming a division answer to the places it needs).
 */
export function rescale(scaled: number, from: number, to: number): number {
    if (to >= from) {
        return scaled * 10 ** (to - from);
    }
    return Math.round(scaled / 10 ** (from - to));
}

/**
 * The fewest places `scaled` can be written at without losing a non-zero digit.
 *
 * Only division needs this: its answer prints at the places it actually has, so
 * 4.5 / 3 is 1.5 rather than 1.50. Everything else keeps its trailing zeros.
 */
export function trailingPlaces(scaled: number, places: number): number {
    let value = Math.abs(scaled);
    let result = places;
    while (result > 0 && value % 10 === 0) {
        value /= 10;
        result--;
    }
    return result;
}
